import React, { useState, useEffect, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import './frontend.css';

const MegaMenuAnimations = ({ container }) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const [isMobile, setIsMobile] = useState(false);
	const [isScrolled, setIsScrolled] = useState(false);
	
	const mobileBreakpoint = 768; // md breakpoint

	// Check if we're on mobile
	const checkMobile = () => {
		setIsMobile(window.innerWidth < mobileBreakpoint);
	};

	// Handle window resize
	useEffect(() => {
		checkMobile();
		window.addEventListener('resize', checkMobile);
		return () => window.removeEventListener('resize', checkMobile);
	}, []);

	// Use Intersection Observer for scroll detection
	useEffect(() => {
		// Create a sentinel element at 50px from top
		const sentinel = document.createElement('div');
		sentinel.style.position = 'absolute';
		sentinel.style.top = '50px';
		sentinel.style.left = '0';
		sentinel.style.width = '1px';
		sentinel.style.height = '1px';
		sentinel.style.pointerEvents = 'none';
		document.body.appendChild(sentinel);

		// Create intersection observer
		const observer = new IntersectionObserver(
			(entries) => {
				const entry = entries[0];
				const scrolled = !entry.isIntersecting;
				console.log('Intersection Observer - isIntersecting:', entry.isIntersecting, 'Setting isScrolled to:', scrolled, 'isMobile:', isMobile);
				setIsScrolled(scrolled);
			},
			{ threshold: 0 }
		);

		observer.observe(sentinel);

		// Cleanup
		return () => {
			observer.disconnect();
			document.body.removeChild(sentinel);
		};
	}, [isMobile]);

	// Handle mobile menu toggle
	const handleToggle = useCallback(() => {
		console.log('Toggle clicked, current state:', isExpanded, 'isMobile:', isMobile);
		const newExpanded = !isExpanded;
		setIsExpanded(newExpanded);
		
		// Update ARIA attributes
		const toggle = container.querySelector('.mega-menu-toggle');
		if (toggle) {
			toggle.setAttribute('aria-expanded', newExpanded);
		}
		
		// Toggle body scroll lock on mobile
		if (isMobile) {
			document.body.style.overflow = newExpanded ? 'hidden' : '';
		}
	}, [isExpanded, isMobile, container]);

	// Handle mobile submenu toggle
	const handleSubmenuToggle = useCallback((event) => {
		event.preventDefault();
		const button = event.currentTarget;
		const submenu = button.nextElementSibling;
		
		if (submenu && submenu.classList.contains('mega-menu-mobile-submenu')) {
			const isExpanded = submenu.classList.contains('expanded');
			
			// Toggle classes
			submenu.classList.toggle('expanded');
			button.classList.toggle('expanded');
			
			// Update ARIA attributes
			button.setAttribute('aria-expanded', !isExpanded);
		}
	}, []);

	// Handle clicks outside menu
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (isMobile && 
				isExpanded && 
				!container.contains(event.target)) {
				handleToggle();
			}
		};

		// Handle backdrop clicks
		const handleBackdropClick = (event) => {
			if (isMobile && 
				isExpanded && 
				event.target.classList.contains('mega-menu-mobile-backdrop')) {
				handleToggle();
			}
		};

		if (isExpanded && isMobile) {
			// Add a small delay to prevent immediate closing when menu opens
			const timeoutId = setTimeout(() => {
				document.addEventListener('click', handleClickOutside);
				document.addEventListener('click', handleBackdropClick);
			}, 100);
			
			return () => {
				clearTimeout(timeoutId);
				document.removeEventListener('click', handleClickOutside);
				document.removeEventListener('click', handleBackdropClick);
			};
		}
		
		return () => {
			document.removeEventListener('click', handleClickOutside);
			document.removeEventListener('click', handleBackdropClick);
		};
	}, [isMobile, isExpanded]);

	// Handle keyboard navigation
	useEffect(() => {
		const handleKeyDown = (event) => {
			if (event.key === 'Escape' && isExpanded) {
				handleToggle();
			}
		};

		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [isExpanded]);

	// Reset mobile states when switching to desktop
	useEffect(() => {
		if (!isMobile) {
			setIsExpanded(false);
			document.body.style.overflow = '';
			
			// Reset DOM classes
			const toggle = container.querySelector('.mega-menu-toggle');
			if (toggle) {
				toggle.setAttribute('aria-expanded', 'false');
			}
		}
	}, [isMobile]);

	// Add event listeners to existing DOM elements
	useEffect(() => {
		const toggle = container.querySelector('.mega-menu-toggle');
		const submenuToggles = container.querySelectorAll('.mega-menu-mobile-submenu-toggle');
		
		console.log('Setting up event listeners, toggle found:', !!toggle, 'submenu toggles:', submenuToggles.length);
		
		// Toggle button
		if (toggle) {
			toggle.addEventListener('click', handleToggle);
			console.log('Toggle event listener added');
		}
		
		// Submenu toggle buttons
		submenuToggles.forEach(button => {
			button.addEventListener('click', handleSubmenuToggle);
		});
		
		// Cleanup
		return () => {
			if (toggle) {
				toggle.removeEventListener('click', handleToggle);
			}
			submenuToggles.forEach(button => {
				button.removeEventListener('click', handleSubmenuToggle);
			});
		};
	}, [handleToggle, handleSubmenuToggle, container]);

	// Add animation classes based on state
	useEffect(() => {
		const mobileMenu = container.querySelector('.mega-menu-mobile');
		const backdrop = container.querySelector('.mega-menu-mobile-backdrop');
		
		if (mobileMenu) {
			if (isExpanded && isMobile) {
				mobileMenu.classList.add('expanded');
			} else {
				mobileMenu.classList.remove('expanded');
			}
		}
		
		if (backdrop) {
			if (isExpanded && isMobile) {
				backdrop.classList.add('visible');
			} else {
				backdrop.classList.remove('visible');
			}
		}
	}, [isExpanded, isMobile]);

	// Apply scroll-based styling
	useEffect(() => {
		// The container parameter IS the mega-menu-container element
		console.log('Scroll effect - isScrolled:', isScrolled, 'isMobile:', isMobile, 'Container:', container);
		if (container) {
			if (isScrolled) {
				console.log('Adding scrolled class');
				container.classList.add('scrolled');
			} else {
				console.log('Removing scrolled class');
				container.classList.remove('scrolled');
			}
		} else {
			console.log('Container not found!');
		}
	}, [isScrolled, isMobile]);

	// This component doesn't render anything - it just manages animations
	return null;
};

// Initialize React components for all mega menu containers
const initMegaMenus = () => {
	const containers = document.querySelectorAll('.mega-menu-container');
	
	containers.forEach(container => {
		// Skip if already initialized
		if (container.dataset.reactInitialized === 'true') return;
		
		// Create a wrapper div for React
		const reactWrapper = document.createElement('div');
		reactWrapper.style.display = 'contents'; // Don't affect layout
		container.appendChild(reactWrapper);
		
		// Create React root and render
		const root = createRoot(reactWrapper);
		root.render(<MegaMenuAnimations container={container} />);
		
		// Mark as initialized
		container.dataset.reactInitialized = 'true';
	});
};

// Initialize when DOM is ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initMegaMenus);
} else {
	initMegaMenus();
}

// Re-initialize on dynamic content changes (for WordPress)
if (typeof MutationObserver !== 'undefined') {
	const observer = new MutationObserver((mutations) => {
		mutations.forEach((mutation) => {
			if (mutation.type === 'childList') {
				initMegaMenus();
			}
		});
	});
	
	observer.observe(document.body, {
		childList: true,
		subtree: true
	});
}